/**
 * CRM réseau par restaurant client (SuperAdmin, brique B) — LOGIQUE PURE.
 *
 * Suit chaque resto CLIENT de Lil & Co (B2B) : contact gérant, date d'ouverture,
 * statut commercial, notes. Aucune I/O ici (cf. crmService.ts).
 */

export type CrmStage = 'prospect' | 'onboarding' | 'active' | 'at_risk' | 'churned';

/** Tous les statuts, dans l'ordre logique du cycle de vie. */
export const CRM_STAGES: CrmStage[] = ['prospect', 'onboarding', 'active', 'at_risk', 'churned'];

export const CRM_STAGE_LABELS: Record<CrmStage, string> = {
  prospect: 'Prospect',
  onboarding: 'Onboarding',
  active: 'Actif',
  at_risk: 'À risque',
  churned: 'Perdu',
};

export type CrmTone = 'good' | 'warn' | 'bad' | 'neutral';

/** Ton d'affichage (couleur du badge) par statut commercial. */
export function crmTone(stage: CrmStage): CrmTone {
  if (stage === 'active') return 'good';
  if (stage === 'at_risk') return 'warn';
  if (stage === 'churned') return 'bad';
  return 'neutral'; // prospect / onboarding
}

export interface CrmInfo {
  managerName: string;
  managerPhone: string;
  managerEmail: string;
  /** Date d'ouverture au format ISO (YYYY-MM-DD), ou ''. */
  openedOn: string;
  stage: CrmStage;
  notes: string;
}

/** CRM vide par défaut. */
export const EMPTY_CRM: CrmInfo = {
  managerName: '',
  managerPhone: '',
  managerEmail: '',
  openedOn: '',
  stage: 'active',
  notes: '',
};

/** Le statut est-il connu ? (garde-fou contre des données legacy). */
export function isValidCrmStage(s: unknown): s is CrmStage {
  return typeof s === 'string' && (CRM_STAGES as string[]).includes(s);
}

/** Normalise une ligne tenant en CrmInfo (champs potentiellement absents/null). */
export function toCrmInfo(row: {
  manager_name?: string | null;
  manager_phone?: string | null;
  manager_email?: string | null;
  opened_on?: string | null;
  crm_stage?: string | null;
  crm_notes?: string | null;
}): CrmInfo {
  return {
    managerName: row.manager_name ?? '',
    managerPhone: row.manager_phone ?? '',
    managerEmail: row.manager_email ?? '',
    openedOn: row.opened_on ?? '',
    stage: isValidCrmStage(row.crm_stage) ? row.crm_stage : 'active',
    notes: row.crm_notes ?? '',
  };
}

/** Email plausible (validation légère, pour signaler une saisie douteuse). */
export function isPlausibleEmail(email: string): boolean {
  if (email.trim() === '') return true; // vide = pas d'erreur (champ optionnel)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** Résumé court d'un contact pour l'affichage en liste (gérant + tel). */
export function contactSummary(info: CrmInfo): string {
  const bits = [info.managerName, info.managerPhone].filter((s) => s.trim() !== '');
  return bits.length ? bits.join(' · ') : 'Contact à compléter';
}
